import React, { useState } from "react";
import { Input } from "antd";
import request from "superagent";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { getAuthHeader } from "../../utils/superagent";
import { getErrorMessage } from "../../utils/errors";
import { logEvent, logError } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { HCF_USER_EVENTS } from "../../constants/firebaseEvents";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { ChatWidgetCard } from "../../appV2/Chat/ChatWidgetCard";
import {
  FACILITY_CANCELED_ME_REQUEST_STATUS_CHIP_MAP,
  formatShiftStartEndDateTime,
  isValidJson,
} from "../../appV2/Chat/utils";
import { Box, Chip, Stack } from "@mui/material";
import { isDefined } from "@clipboard-health/util-ts";
import { AdminMessage } from "@sendbird/chat/message";
import { z } from "zod";
import { useSession } from "../../appV2/Auth/api/useSession";
import { PACIFIC_TIME_ZONE } from "../../appV2/Timezone/constants";
import { useGetShift } from "../ShiftSignoutFlow/api/useGetShift";
import { Avatar } from "../../appV2/redesign/components/Avatar";
import { useAgentProfilePicture } from "../../appV2/ShiftBlock/hooks/useGetAgentProfilePicture";
import { getPlacementChatContextFromChannel } from "./utils/placementChat";
import { Button } from "../../appV2/redesign/components/Button";
import {
  DESTRUCTIVE_BUTTON_SX,
  PRIMARY_BUTTON_SX,
} from "../../appV2/redesign/components/Button.styles";
import { CheckBoxWrapper } from "../../components/CheckBoxWrapper";
import { Modal, Checkbox } from "antd";

const { TextArea } = Input;

const shareAlert = (
  <div style={{ marginTop: 8 }}>
    <ExclamationCircleOutlined
      style={{
        color: "#868686",
        opacity: 0.6,
      }}
    />
    <h5
      style={{
        display: "inline",
        marginLeft: 8,
        color: "#868686",
        opacity: 0.6,
      }}
    >
      Please note this reason will be shared with the worker.
    </h5>
  </div>
);

interface ReviewSentHomeMessageProps {
  message: AdminMessage;
  facilityId: string;
  facilityUserId?: string;
}

const messagePayloadSchema = z.object({
  shiftId: z.string(),
  approvalStatus: z.number(),
  description: z.string().optional(),
  workerName: z.string().optional(),
});

type MessagePayload = z.infer<typeof messagePayloadSchema>;

export function ReviewSentHomeMessage({
  message,
  facilityId,
  facilityUserId,
}: ReviewSentHomeMessageProps) {
  const [visibleRejectModal, setVisibleRejectModal] = useState(false);
  const [visibleApproveModal, setVisibleApproveModal] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationFail, setValidationFail] = useState(false);
  const [isCancellationAcknowledged, setCancellationIsAcknowledged] = useState(false);
  const { profile } = useSession();
  const { workerId } = getPlacementChatContextFromChannel(message.channelUrl ?? "");

  function extractNameFromMessageText(messageText: string) {
    const match = messageText.match(/Can you confirm your facility sent (.+?) home\?/);
    if (!isDefined(match)) {
      logError(APP_V2_APP_EVENTS.EXTRACT_FACILITY_CANCELED_ME_MESSAGE_NAME_FAILURE, {
        error: new Error("Regex match failed for name extraction"),
        metadata: { messageText },
      });
      return undefined;
    }
    return match[1].trim();
  }

  function extractDescriptionFromMessageText(messageText: string) {
    // Message format: "They chose [person] as the responsible representative
    // and provided the following details with this request: [reason]. Can..."
    const match = messageText.match(
      /provided the following details with this request: (.+?)\. Can/
    );
    if (!isDefined(match)) {
      logError(APP_V2_APP_EVENTS.EXTRACT_FACILITY_CANCELED_ME_MESSAGE_REASON_FAILURE, {
        error: new Error("Regex match failed for description extraction"),
        metadata: { messageText },
      });
      return undefined;
    }
    return match[1].trim();
  }

  let metaData: MessagePayload | undefined;

  if (!isValidJson(message.data)) {
    logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
      error: new Error("Failed to parse message data: invalid JSON"),
      metadata: { messageId: message.messageId },
    });
  } else {
    try {
      const parsed = JSON.parse(message.data);
      const validationResult = messagePayloadSchema.safeParse(parsed);
      if (!validationResult.success) {
        logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
          error: new Error("Failed to validate message data: missing required fields"),
          metadata: {
            messageId: message.messageId,
            validationResult,
          },
        });
      } else {
        metaData = validationResult.data as MessagePayload;

        // Messages generated prior to INV-2821 are not expected to include
        // workerName and description in their payload, but we can extract these
        // properties from the message text as a fallback
        const messageText = message.message;
        if (!isDefined(metaData.workerName)) {
          metaData.workerName = extractNameFromMessageText(messageText);
        }
        if (!isDefined(metaData.description)) {
          metaData.description = extractDescriptionFromMessageText(messageText);
        }
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.EXTRACT_MESSAGE_DATA_FAILURE, {
        error: new Error("Failed to parse message data"),
        metadata: { error, messageId: message.messageId },
      });
    }
  }

  const { data: shiftResponse } = useGetShift(metaData?.shiftId ?? "", {
    enabled: isDefined(metaData?.shiftId),
  });

  const { data: profilePicture } = useAgentProfilePicture(
    { agentId: workerId ?? "" },
    { enabled: isDefined(workerId) }
  );

  if (!isDefined(metaData) || !isDefined(metaData?.workerName)) {
    logError(APP_V2_APP_EVENTS.REVIEW_SENT_HOME_MESSAGE_RENDER_FAILURE, {
      error: new Error("Missing required data for review sent home message"),
      metadata: {
        messageId: message.messageId,
      },
    });
    return null;
  }

  const shift = shiftResponse?.response;
  const shiftTimeZone = shift?.facility?.tmz ?? profile?.tmz ?? PACIFIC_TIME_ZONE;

  const formattedShiftTime =
    isDefined(shift?.start) && isDefined(shift?.end)
      ? formatShiftStartEndDateTime(shift.start, shift.end, shiftTimeZone)
      : "";

  const eventPayload = {
    trigger: metaData.approvalStatus !== -1 ? "updateResponse" : "addConfirmation",
    shiftId: metaData.shiftId,
    workerId,
    workplaceId: facilityId,
    workplaceUserId: facilityUserId,
    eventName: "facility_cancelled_me_confirmation",
  };

  const rejectRequest = async () => {
    if (!reason || !isCancellationAcknowledged) {
      setValidationFail(true);
      return;
    }
    setLoading(true);
    try {
      await request
        .patch(`${global.api}/shifts/${metaData.shiftId}/facility-cancelled-me/reject`)
        .set(await getAuthHeader())
        .send({
          messageId: String(message.messageId),
          isUpdateResponse: metaData.approvalStatus !== -1,
          reason,
        });
      setLoading(false);
      setVisibleRejectModal(false);
      showSuccessToast("Thanks for your response!");

      logEvent(HCF_USER_EVENTS.FACILITY_CANCELLED_ME_CONFIRMATION_SUBMITTED, {
        ...eventPayload,
        action: "reject",
        reason,
        success: true,
      });
    } catch (error) {
      showErrorToast(getErrorMessage(error));
      setLoading(false);
      logEvent(HCF_USER_EVENTS.FACILITY_CANCELLED_ME_CONFIRMATION_SUBMITTED, {
        ...eventPayload,
        action: "reject",
        reason,
        success: false,
      });
    }
  };

  const approveRequest = async () => {
    if (metaData.approvalStatus === 0 && !reason) {
      setValidationFail(true);
      return;
    }
    setLoading(true);
    try {
      await request
        .patch(`${global.api}/shifts/${metaData.shiftId}/facility-cancelled-me/approve`)
        .set(await getAuthHeader())
        .send({
          messageId: String(message.messageId),
          isUpdateResponse: metaData.approvalStatus !== -1,
          reason,
        });
      setLoading(false);
      setVisibleApproveModal(false);
      showSuccessToast("Thanks for your response!");

      logEvent(HCF_USER_EVENTS.FACILITY_CANCELLED_ME_CONFIRMATION_SUBMITTED, {
        ...eventPayload,
        action: "approve",
        reason,
        success: true,
      });
    } catch (error) {
      showErrorToast(getErrorMessage(error));
      setLoading(false);
      logEvent(HCF_USER_EVENTS.FACILITY_CANCELLED_ME_CONFIRMATION_SUBMITTED, {
        ...eventPayload,
        action: "approve",
        reason,
        success: false,
      });
    }
  };

  const title = `${metaData.workerName} submitted a sent home request`;
  const subtitle = formattedShiftTime;

  const AVATAR_SIZE = 28;

  const isRequestPending = metaData.approvalStatus === -1;

  const chipProps = FACILITY_CANCELED_ME_REQUEST_STATUS_CHIP_MAP[metaData.approvalStatus];

  const actionButtons = isRequestPending ? (
    <Stack direction="row" spacing={1} width="100%">
      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          setVisibleApproveModal(true);
        }}
        sx={{
          // oxlint-disable-next-line typescript/no-misused-spread
          ...PRIMARY_BUTTON_SX,
          flex: 1,
        }}
      >
        Accept
      </Button>
      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          setVisibleRejectModal(true);
        }}
        sx={{
          // oxlint-disable-next-line typescript/no-misused-spread
          ...DESTRUCTIVE_BUTTON_SX,
          flex: 1,
        }}
      >
        Decline
      </Button>
    </Stack>
  ) : null;

  return (
    <Stack
      width="100%"
      direction="row"
      spacing={1}
      alignItems="flex-end"
      className="sendbird-admin-message"
      sx={{ justifyContent: "flex-start" }}
    >
      <Box width={AVATAR_SIZE}>
        <Avatar
          alt={metaData.workerName || "User"}
          src={profilePicture?.distribution_url}
          sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
        />
      </Box>

      <ChatWidgetCard
        title={title}
        subtitle={subtitle}
        caption={metaData.description}
        isIncomingMessage={true}
        statusChip={
          <Chip
            label={chipProps.label}
            aria-label={chipProps.label}
            color={chipProps.color}
            variant="outlined"
            sx={{
              border: "none",
              borderRadius: 2,
              fontWeight: "bold",
              ...chipProps.customStyles,
            }}
          />
        }
        actions={actionButtons}
      />

      <Modal
        okText={`Yes, approve`}
        okType={"primary"}
        cancelText={"No, don't approve"}
        title="Approve this shift as a sent home?"
        visible={visibleApproveModal}
        onOk={approveRequest}
        onCancel={() => {
          setVisibleApproveModal(false);
          setReason("");
          setValidationFail(false);
          setLoading(false);
        }}
        okButtonProps={{
          loading,
          disabled: loading,
        }}
        destroyOnClose={true}
      >
        <h5>Reason for approval</h5>
        <TextArea
          required={true}
          placeholder={
            metaData.approvalStatus === -1
              ? "You may provide context for your approval reason"
              : "Please provide context for your approval reason"
          }
          rows={4}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value) {
              setValidationFail(false);
            }
          }}
          status={validationFail ? "error" : ""}
        />
        {validationFail ? (
          <h5 style={{ color: "red", marginTop: 9 }}>Please fill out reason for approval.</h5>
        ) : (
          shareAlert
        )}
      </Modal>

      <Modal
        okText={`Yes, reject`}
        okType={"danger"}
        cancelText={"No, don't reject"}
        title="Reject this shift as a sent home?"
        visible={visibleRejectModal}
        onOk={rejectRequest}
        onCancel={() => {
          setVisibleRejectModal(false);
          setReason("");
          setValidationFail(false);
          setLoading(false);
        }}
        okButtonProps={{
          loading,
          disabled: loading,
        }}
        destroyOnClose={true}
      >
        <h5>Reason for rejection</h5>
        <TextArea
          required={true}
          data-testid="sent-home-reason-textarea"
          placeholder="Please provide context for your rejection reason"
          rows={4}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value) {
              setValidationFail(false);
            }
          }}
          status={validationFail && !reason ? "error" : ""}
        />
        {validationFail && !reason ? (
          <h5 style={{ color: "red", marginTop: 9 }}>Please fill out reason for rejection.</h5>
        ) : (
          shareAlert
        )}
        <CheckBoxWrapper className={!isCancellationAcknowledged && validationFail ? "invalid" : ""}>
          <Checkbox
            id="accept"
            aria-required="true"
            checked={isCancellationAcknowledged}
            onChange={(e) => setCancellationIsAcknowledged(e.target.checked)}
            data-testid="cancellation-acknowledged-check-box"
          >
            I understand that rejecting this sent home request will reflect on the worker as
            abandoning their shift without my workplace's approval or not showing up for their
            shift. My action will mark this shift as a no show and result in the professional's
            restriction. I confirm the information I have provided is correct and true.
          </Checkbox>
        </CheckBoxWrapper>
      </Modal>
    </Stack>
  );
}
